import { z } from "zod";
import { applicantSchema } from "../schemas/applicantSchema";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { Button, Input, Stack } from "@chakra-ui/react";
import { useGetCompanies } from "@/core/companies/useCases/useGetCompanies";
import { useAuth } from "@/shared/session";
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "@/ui/chakra/dialog";
import { Field } from "@/ui/chakra/field";
import { NativeSelectField, NativeSelectRoot } from "@/ui/chakra/native-select";
import { SimilarRecordsHint } from "@/ui/SimilarRecordsHint";
import { useSimilarApplicants } from "../useCases/useSimilarApplicants";

type ApplicantFormInput = z.infer<typeof applicantSchema>;
interface CreateApplicantModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: ApplicantFormInput) => void;
  isSubmitting?: boolean;
}

export const CreateApplicantModal = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false,
}: CreateApplicantModalProps) => {
  const { user, session } = useAuth();

  const zones = user?.zones;

  const { data: Companies, isPending: isCompaniesLoading } = useGetCompanies();
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<ApplicantFormInput>({
    resolver: zodResolver(applicantSchema),
    defaultValues: {
      name: "",
      email: undefined,
      phone: "",
      companyId: "",
      zoneId: "",
      enabled: true,
    },
  });

  const similarApplicants = useSimilarApplicants({
    name: watch("name"),
    email: watch("email"),
    phone: watch("phone"),
    zoneId: watch("zoneId") || session?.zone || undefined,
  });

  useEffect(() => {
    if (isOpen) reset();
  }, [isOpen, reset]);

  const onFormSubmit = (data: ApplicantFormInput) => {
    onSubmit(data);
  };

  return (
    <DialogRoot isOpen={isOpen} onClose={onClose} isCentered size="md">
      <DialogContent>
        <form onSubmit={handleSubmit(onFormSubmit)}>
          <DialogHeader>
            <DialogTitle>Crear solicitante</DialogTitle>
          </DialogHeader>
          <DialogCloseTrigger />
          <DialogBody>
            <Stack gap={4}>
              <Field
                label="Nombre"
                required
                invalid={!!errors.name}
                errorText={errors.name?.message}
              >
                <Input
                  {...register("name")}
                  placeholder="Nombre"
                  autoComplete="off"
                />
              </Field>
              <SimilarRecordsHint
                items={similarApplicants}
                entityLabel="solicitantes"
                mt={-2}
              />

              <Field
                label="Correo"
                invalid={!!errors.email}
                errorText={errors.email?.message}
              >
                <Input
                  {...register("email")}
                  placeholder="correo@belray.cl"
                  autoComplete="off"
                />
              </Field>

              <Field label="Teléfono">
                <Input
                  {...register("phone")}
                  placeholder="974015393"
                  autoComplete="off"
                />
              </Field>

              <Field
                label="Empresa"
                required
                invalid={!!errors.companyId}
                errorText={errors.companyId?.message}
              >
                <NativeSelectRoot disabled={isCompaniesLoading}>
                  <NativeSelectField
                    {...register("companyId")}
                    placeholder="Seleccione una empresa"
                  >
                    {Companies?.map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))}
                  </NativeSelectField>
                </NativeSelectRoot>
              </Field>

              <Field
                label="Zona"
                required
                invalid={!!errors.zoneId}
                errorText={errors.zoneId?.message}
              >
                <NativeSelectRoot>
                  <NativeSelectField
                    {...register("zoneId")}
                    placeholder="Seleccione una zona"
                  >
                    {zones?.map((zone) => (
                      <option key={zone.id} value={zone.id}>
                        {zone.name}
                      </option>
                    ))}
                  </NativeSelectField>
                </NativeSelectRoot>
              </Field>
            </Stack>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={onClose} type="button">
              Cancelar
            </Button>
            <Button colorPalette="brand" type="submit" loading={isSubmitting}>
              Guardar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </DialogRoot>
  );
};
